import { IAppCustomDates } from "../../../utils/types";
import { WriteOffCodes } from "../../../utils/types/billing";
import { ClaimStatus, IClaimActivity } from "../../claims/types/claims.types";

export interface AdjustmentProps {
    no: string;
    group: string;
    description: string;
    amount: number;
    quantity: number;
    total: number;
}

export interface InsuranceProps {
    description: string;
    payment_date: string;
    payment_type: string;
    amount: number;
    payment_method: string;
    date_posted: string;
    payment_notes: string;
    bill_status: string;
    auth_code: string;
    payment_status: string;
}

export enum RemitStatus {
    POSTED = "posted",
    UN_POSTED = "unposted",
    ON_HOLD = "on_hold",
}

export enum RemitSource {
    MANUAL = "Manual",
    ELECTRONIC = "Electronic",
}

export interface IBatchRemitAssignee {
    remittance_batch_id: string;
    practice_id: string;
    created_at: string;
    status: string;
    notes: string;
    user: {
        id: string;
        first_name: string;
        last_name: string;
    };
}

export interface IBatchRemit {
    remittance_batch_id: string;
    insurance_provider_name: string;
    remit_received_date: string;
    remit_batch_status: RemitStatus;
    remit_source: RemitSource;
    remit_amount: number;
    no_of_claims_payments: number;
    is_system_created: boolean;
    no_of_unmatched_payments: number;
    no_of_matched_payments: number;
    remit_batch_payment_ref: string;
    remit_batch_ref_id: string;
    practice_id: string;
    no_of_claims_payments_from_file: number;
    amount_of_unmatched_payments: number;
    amount_of_matched_payments: number;
    remit_batch_eob_url: string;
    no_of_claims: number;
    remit_batch_file_url?: string;
    insurance_provider_id?: string;
    batch_remit_assignees: IBatchRemitAssignee[];
    unmatched_remits: IUnmatchedRemitLine[] | null;
}

export interface IUnmatchedRemitLine {
    remittance_id: string;
    claim_to_match: string | null;
    cpt_code: string | null;
    paid_amount: number;
    patient_name: string | null;
}

export interface IBatchRemitFilters {
    batch_remit_status?: string[];
    batch_remit_id?: string;
    has_payments?: string;
    has_claims?: string;
    insurance_provider_ids?: string[];
    from_date?: string | null;
    to_date?: string | null;
    page?: number;
    limit?: number;
    sort_attribute?: string;
    sort_order?: string;
    remit_source?: string;
    search_string?: string;
    dos_from_date?: string | null;
    dos_to_date?: string | null;
}
export interface IAddManualRemit {
    remit_batch_payment_ref: string;
    insurance_provider_id: string;
    no_of_claims_payments: number;
    remit_date: string;
    amount: number;
}

export interface IAddRemitClaims {
    claim_ids: string[];
    remit_id?: string;
}

export interface IMatchElectronicRemitClaim {
    claim_id: string;
    claim_to_match: string;
}

export interface IClaimsReport {
    claim_id: string;
    reason: string;
}
export interface IAddRemitResponse {
    message: string;
    remittance_batch_id: string;
    added_claims: string[];
    unadded_claims: IClaimsReport[];
}
export interface IClaimAdjustments {
    quantity: number;
    group_code: string;
    amount: number;
    reason_code: string;
    reason_description: string;
    amount_2: number;
    reason_code_2: string;
    reason_description_2: string;
}

export interface RemitAdjustmentRow {
    id: string;
    cpt: string;
    code: string;
    groupCode: string;
    description: string;
    amount: number;
}

export interface IInsurancePayments {
    payment_id: string;
    payment_description: string;
    date_posted: string;
    payment_type: string;
    payment_date: string;
    payment_method: string;
    amount: number;
    payment_notes: string;
    invoice_status: string;
    auth_code: string;
    channel?: string;
    processor_payment_status: string;
    payment_status: string;
    cheque_number?: string;
    writeoff_code?: WriteOffCodes;
    remark_codes: string[];
    remittance_id: string | null;
    claim_id: string | null;
    remark_codes_payment?: Record<string, number | null> | null;
    payment_info?: {
        refund_reason?: string;
        cheque_number?: string;
        authorization_number?: string;
    };
    posted_by: {
        first_name: string;
        last_name: string;
    };
    eft_number?: string;
}

export interface IRemit {
    is_archived: boolean;
    archived_notes: string | null;
    remittance_batch_id: string;
    remittance_id: string;
    claim_id: string;
    claim_status_code: string;
    claim_status_description: string;
    remit_payment_date: string;
    remit_received_date?: string | null;
    date_of_service: string;
    claim_number: string;
    claim_status: ClaimStatus;
    claim_activities: IClaimActivity[] | null;
    batch_remit_assignees: IBatchRemitAssignee[];
    client: {
        first_name: string;
        last_name: string;
        client_id: string;
    };
    cpt_code: string;
    remittance_cpt_code: string | null;
    charged: number;
    note_id: string;
    paid_amount: number;
    ucr?: string;
    outstanding: number;
    expected_amount: number;
    patient_responsibility_amount: number;
    remit_status: RemitStatus;
    invoice_id: string;
    era_blob: string;
    has_eob: boolean;
    claim_adjustments: IClaimAdjustments[];
    insurance_payments: IInsurancePayments[];
    insurance_provider_name: string;
    insurance_expected: number;
    patient_paid: number;
    patient_outstanding: number;
    insurance_outstanding: number;
    insurance_charged: number;
    insurance_paid: number;
    patient_charged: number;
    total_outstanding: number;
    patient_writeoff: number;
    interest_collected: number;
    interest_refund: number;
    insurance_writeoff: number;
    patient_accounting: number;
    insurance_accounting: number;
    pr100_collected: number;
    remit_source: RemitSource;
    remit_batch_eob_url?: string | null;
    remit_batch_payment_ref?: string | null;
    manually_linked: boolean | null;
    is_system_created: boolean;
}

export interface IRemitsFilters {
    batch_remit_id?: string;
    note_id?: string;
    search_string?: string;
    claim_status?: string;
    remittance_id?: string;
    page?: number;
    limit?: number;
    claim_id?: string;
    sort_attribute?: string;
    sort_order?: string;
    from_date?: string | null;
    to_date?: string | null;
    custom_date?: IAppCustomDates;
    show_archived_remits?: string;
    payment_status?: string;
}

export interface IUpdateRemitStatus {
    remittance_batch_ids: string[];
    status?: string;
    insurance_provider_id?: string;
}

export interface IRemitDocument {
    remittance_attachment_id: string;
    url: string;
    name: string;
    metadata: {
        content_type: string;
    } | null;
}

export interface IArchiveRemitPayload {
    remit_ids_to_archive: string[];
    archive_notes?: string;
    archive_status: boolean;
}

export interface IReassignRemitClaimLinksPayload {
    source_claim_id: string;
    target_claim_id: string;
    remittance_ids?: string[];
    reason: string;
}

export interface IReassignRemitClaimLinksResult {
    source_claim_id: string;
    target_claim_id: string;
    source_claim_status: ClaimStatus;
    target_claim_status: ClaimStatus;
    remittance_ids: string[];
    updated_remittances_count: number;
    updated_payments_count: number;
    updated_unmatched_payments_count: number;
}

export interface IDeleteManualBatchRemit {
    batch_remit_ids: string[];
}

export interface IPostPaymentsPayload {
    payment_ids: string[];
}

export interface IPostPaymentsResponse {
    posted_payments: string[];
    unposted_payments: { payment_id: string; reason: string }[];
}

export interface IDeleteManualClaimPayload {
    claim_ids: string[];
    remittance_ids?: string[];
}

export interface IUpdateBatchRemitAssignee {
    remittance_batch_ids: string[];
    status: string;
    notes?: string;
}
